import { resolveRegularMaterial, regularMaterialDisplayName } from './regular-materials-20261025.js';
import { REGULAR_FABRIC_DETAILS } from './regular-fabric-detail-data-20261026.js';

// Read the existing configurator selection without changing its form or commerce state.
const bindings = new WeakMap();
const copy = {
  en: {
    premium: 'Selected fabric',
    view: 'View fabric detail',
    illustration: 'Fabric detail illustration',
    original: 'Original catalogue fabric sample',
    sample: 'Catalogue fabric sample',
    note: 'Screen colours may vary. See fabric samples at our Markham showroom.',
    contact: 'Markham showroom · Contact us',
    close: 'Close fabric detail',
    zoomIn: '+ Zoom in',
    zoomOut: '− Fit image',
    families: { zebra: 'Custom Zebra Blinds', roller: 'Custom Roller Shades', sheer: 'Custom Sheer Shades' },
    types: { light_filtering: 'Light Filtering', opaque: 'Blackout' },
  },
  fr: {
    premium: 'Tissu choisi',
    view: 'Voir le tissu',
    illustration: 'Illustration du détail du tissu',
    original: 'Échantillon original du catalogue',
    sample: 'Échantillon du catalogue',
    note: 'Les couleurs à l’écran peuvent varier. Venez voir les échantillons de tissus dans notre salle d’exposition à Markham.',
    contact: 'Salle d’exposition de Markham · Contactez-nous',
    close: 'Fermer le détail du tissu',
    zoomIn: '+ Agrandir',
    zoomOut: '− Ajuster l’image',
    families: { zebra: 'Stores alternés sur mesure', roller: 'Toiles à rouleau sur mesure', sheer: 'Stores à voilage sur mesure' },
    types: { light_filtering: 'Filtrant la lumière', opaque: 'Occultant' },
  },
  zh: {
    premium: '已选面料',
    view: '查看面料细节',
    illustration: '面料细节示意图',
    original: '目录原始面料样品',
    sample: '目录面料样品',
    note: '屏幕显示可能存在色差，欢迎到万锦展厅查看实物面料。',
    contact: '万锦展厅 · 联系我们',
    close: '关闭面料细节',
    zoomIn: '+ 放大',
    zoomOut: '− 适应画面',
    families: { zebra: '定制斑马帘', roller: '定制卷帘', sheer: '定制香格里拉帘' },
    types: { light_filtering: '透光', opaque: '遮光' },
  },
};

export function regularFabricPresentation(family, type, materialId, language) {
  const locale = language?.toLowerCase().startsWith('zh') ? 'zh' : language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  const text = copy[locale];
  const material = resolveRegularMaterial(family, type, materialId);
  if (!material?.image) return null;
  const entry = REGULAR_FABRIC_DETAILS[material.id];
  const illustration = entry?.kind === 'illustration' && typeof entry.image === 'string' && entry.image.trim();
  const detail = illustration ? entry.image : material.image;
  const fabricName = regularMaterialDisplayName(material, locale);
  return {
    key: JSON.stringify([material.id, type, detail]),
    selected: `${fabricName} · ${text.families[family]} · ${text.types[type]}`,
    detail,
    detailLabel: illustration ? text.illustration : text.original,
    swatch: material.thumbnail || material.image,
  };
}

function bindFabricDetail(component) {
  if (bindings.has(component)) return;
  const configurator = component.closest('.configurator');
  const selectionImage = configurator?.querySelector('#config-image');
  const familySelect = configurator?.querySelector('#product-family');
  const dialog = component.querySelector('[data-fabric-dialog]');
  const openers = Array.from(configurator?.querySelectorAll('[data-fabric-open]') || []);
  const closer = component.querySelector('[data-fabric-close]');
  const zoom = component.querySelector('[data-fabric-zoom]');
  const viewport = component.querySelector('[data-fabric-viewport]');
  const detail = component.querySelector('[data-fabric-image]');
  const swatches = configurator?.querySelectorAll('[data-fabric-swatch]') || [];
  if (!selectionImage || !familySelect || !dialog || !openers.length || !closer || !zoom || !viewport || !detail) return;

  let text = copy.en;
  let currentKey = '';
  let returnFocus = null;
  let syncState = {};
  const setZoom = expanded => {
    viewport.classList.toggle('is-zoomed', expanded);
    zoom.setAttribute('aria-pressed', String(expanded));
    zoom.textContent = expanded ? text.zoomOut : text.zoomIn;
    if (!expanded) { viewport.scrollTop = 0; viewport.scrollLeft = 0; }
  };
  const update = () => {
    const language = document.documentElement.lang.toLowerCase().split('-')[0];
    const locale = language === 'zh' ? 'zh' : language === 'fr' ? 'fr' : 'en';
    text = copy[locale];
    const selectedFamily = syncState.family || familySelect.value;
    const family = Object.hasOwn(text.families, selectedFamily) ? selectedFamily : 'zebra';
    const activeType = syncState.type || configurator.querySelector('.type-option[aria-pressed="true"]')?.dataset.lightType;
    const type = activeType === 'opaque' ? 'opaque' : 'light_filtering';
    const materialId = syncState.materialId ?? configurator.dataset.regularMaterialId ?? '';
    const image = regularFabricPresentation(family, type, materialId, locale);
    for (const opener of openers) opener.disabled = !image;
    if (!image?.detail || !image?.swatch) return false;
    const { key, selected } = image;
    for (const node of configurator.querySelectorAll('[data-fabric-copy]')) {
      node.textContent = node.dataset.fabricCopy === 'illustration' ? image.detailLabel : text[node.dataset.fabricCopy] || '';
    }
    for (const opener of openers) opener.setAttribute('aria-label', `${text.view} · ${selected} · ${text.premium}`);
    for (const node of component.querySelectorAll('[data-fabric-selection]')) node.textContent = selected;
    if (detail.getAttribute('src') !== image.detail) detail.setAttribute('src', image.detail);
    detail.alt = `${selected} · ${image.detailLabel}`;
    for (const swatch of swatches) {
      if (swatch.getAttribute('src') !== image.swatch) swatch.setAttribute('src', image.swatch);
      if (swatch.hasAttribute('data-fabric-original')) swatch.alt = `${selected} · ${text.sample}`;
    }
    closer.setAttribute('aria-label', text.close);
    viewport.setAttribute('aria-label', image.detailLabel);
    zoom.textContent = viewport.classList.contains('is-zoomed') ? text.zoomOut : text.zoomIn;
    if (key !== currentKey) { currentKey = key; setZoom(false); }
    return true;
  };
  const open = event => {
    if (!update()) return;
    if (dialog.open) return;
    returnFocus = event.currentTarget || document.activeElement;
    setZoom(false);
    dialog.showModal();
    document.body.classList.add('dialog-open');
    closer.focus({ preventScroll: true });
  };
  const close = () => { if (dialog.open) dialog.close(); };
  const restoreFocus = () => {
    setZoom(false);
    if (!document.querySelector('dialog[open]')) document.body.classList.remove('dialog-open');
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    returnFocus = null;
  };
  const backdropClose = event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
  };
  const toggleZoom = () => setZoom(!viewport.classList.contains('is-zoomed'));
  for (const opener of openers) opener.addEventListener('click', open);
  closer.addEventListener('click', close);
  zoom.addEventListener('click', toggleZoom);
  dialog.addEventListener('click', backdropClose);
  // Native dialog handles Escape, focus containment, and inert background content.
  dialog.addEventListener('close', restoreFocus);
  const sync = event => { syncState = { ...syncState, ...event.detail }; update(); };
  document.addEventListener('evereno:regular-material-sync', sync);
  const observer = new MutationObserver(update);
  observer.observe(selectionImage, { attributes: true, attributeFilter: ['src'] });
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });
  const dispose = event => {
    if (!event.target.contains(component)) return;
    close();
    restoreFocus();
    observer.disconnect();
    document.removeEventListener('evereno:regular-material-sync', sync);
    for (const opener of openers) opener.removeEventListener('click', open);
    closer.removeEventListener('click', close);
    zoom.removeEventListener('click', toggleZoom);
    dialog.removeEventListener('click', backdropClose);
    dialog.removeEventListener('close', restoreFocus);
    document.removeEventListener('shopify:section:unload', dispose);
    bindings.delete(component);
  };
  document.addEventListener('shopify:section:unload', dispose);
  bindings.set(component, { observer });
  update();
}

// The included white square cassette has its own viewer, independent of fabric selection.
const cassetteBindings = new WeakMap();
const cassetteCopy = {
  en: {
    title: 'White Aluminum-Alloy Cassette',
    material: 'Aluminum-alloy cassette',
    view: 'View cassette detail',
    illustration: 'Product detail illustration',
    note: 'Screen colours may vary. See fabric samples at our Markham showroom.',
    contact: 'Markham showroom · Contact us',
    close: 'Close cassette detail',
    zoomIn: '+ Zoom in',
    zoomOut: '− Fit image',
  },
  fr: {
    title: 'Boîtier blanc en alliage d’aluminium',
    material: 'Boîtier en alliage d’aluminium',
    view: 'Voir le boîtier',
    illustration: 'Illustration du détail du produit',
    note: 'Les couleurs à l’écran peuvent varier. Venez voir les échantillons de tissus dans notre salle d’exposition à Markham.',
    contact: 'Salle d’exposition de Markham · Contactez-nous',
    close: 'Fermer le détail du boîtier',
    zoomIn: '+ Agrandir',
    zoomOut: '− Ajuster l’image',
  },
  zh: {
    title: '白色铝合金帘盒',
    material: '铝合金帘盒',
    view: '查看帘盒细节',
    illustration: '产品细节示意图',
    note: '屏幕显示可能存在色差，欢迎到万锦展厅查看实物面料。',
    contact: '万锦展厅 · 联系我们',
    close: '关闭帘盒细节',
    zoomIn: '+ 放大',
    zoomOut: '− 适应画面',
  },
};

function bindCassetteDetail(component) {
  if (cassetteBindings.has(component)) return;
  const configurator = component.closest('.configurator');
  const openers = Array.from(configurator?.querySelectorAll('[data-cassette-open]') || []);
  const dialog = component.querySelector('[data-cassette-dialog]');
  const closer = component.querySelector('[data-cassette-close]');
  const zoom = component.querySelector('[data-cassette-zoom]');
  const viewport = component.querySelector('[data-cassette-viewport]');
  const image = component.querySelector('[data-cassette-image]');
  if (!dialog || !openers.length || !closer || !zoom || !viewport || !image) return;

  let text = cassetteCopy.en;
  let returnFocus = null;
  const setZoom = expanded => {
    viewport.classList.toggle('is-zoomed', expanded);
    zoom.setAttribute('aria-pressed', String(expanded));
    zoom.textContent = expanded ? text.zoomOut : text.zoomIn;
    if (!expanded) { viewport.scrollTop = 0; viewport.scrollLeft = 0; }
  };
  const update = () => {
    const language = document.documentElement.lang.toLowerCase().split('-')[0];
    const locale = language === 'zh' ? 'zh' : language === 'fr' ? 'fr' : 'en';
    text = cassetteCopy[locale];
    for (const node of configurator.querySelectorAll('[data-cassette-copy]')) {
      node.textContent = text[node.dataset.cassetteCopy] || '';
    }
    for (const opener of openers) opener.setAttribute('aria-label', `${text.view} · ${text.title}`);
    image.alt = `${text.title} · ${text.illustration}`;
    closer.setAttribute('aria-label', text.close);
    viewport.setAttribute('aria-label', `${text.title} · ${text.illustration}`);
    zoom.textContent = viewport.classList.contains('is-zoomed') ? text.zoomOut : text.zoomIn;
  };
  const open = event => {
    update();
    if (dialog.open) return;
    returnFocus = event.currentTarget || document.activeElement;
    setZoom(false);
    dialog.showModal();
    document.body.classList.add('dialog-open');
    closer.focus({ preventScroll: true });
  };
  const close = () => { if (dialog.open) dialog.close(); };
  const restoreFocus = () => {
    setZoom(false);
    if (!document.querySelector('dialog[open]')) document.body.classList.remove('dialog-open');
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    returnFocus = null;
  };
  const backdropClose = event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
  };
  const toggleZoom = () => setZoom(!viewport.classList.contains('is-zoomed'));
  for (const opener of openers) opener.addEventListener('click', open);
  closer.addEventListener('click', close);
  zoom.addEventListener('click', toggleZoom);
  dialog.addEventListener('click', backdropClose);
  // Native dialog handles Escape, focus containment, and the inert page backdrop.
  dialog.addEventListener('close', restoreFocus);
  const observer = new MutationObserver(update);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });
  const dispose = event => {
    if (!event.target.contains(component)) return;
    close();
    restoreFocus();
    observer.disconnect();
    for (const opener of openers) opener.removeEventListener('click', open);
    closer.removeEventListener('click', close);
    zoom.removeEventListener('click', toggleZoom);
    dialog.removeEventListener('click', backdropClose);
    dialog.removeEventListener('close', restoreFocus);
    document.removeEventListener('shopify:section:unload', dispose);
    cassetteBindings.delete(component);
  };
  document.addEventListener('shopify:section:unload', dispose);
  cassetteBindings.set(component, { observer });
  update();
}

function initialize(scope = document) {
  if (scope.matches?.('[data-fabric-details]')) bindFabricDetail(scope);
  scope.querySelectorAll('[data-fabric-details]').forEach(bindFabricDetail);
  if (scope.matches?.('[data-cassette-details]')) bindCassetteDetail(scope);
  scope.querySelectorAll('[data-cassette-details]').forEach(bindCassetteDetail);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
}
